// src/components/templates/TemplateCard.jsx
//
// One template in the gallery grid: a format mockup as the thumbnail, the name
// and description, and two actions — expand the card to see its structure, or
// use it to start a draft.
//
// The preview expands **in place** rather than opening a modal. An author
// comparing the Three-Act Novel against the Hero's Journey wants both open at
// once; a dialog makes that two round trips and a remembered impression.
import React from 'react';
import { Box, Button, Typography } from '@mui/material';

import FormatSample from './FormatSample';
import {
  SP,
  FONT,
  CATEGORY_LABEL,
  TEMPLATE_CATEGORY_COLOR,
  THUMB_BG,
} from './templateTokens';

export default function TemplateCard({ template, expanded, onToggleExpand, onUse }) {
  const accent = TEMPLATE_CATEGORY_COLOR[template.category] || SP.purple;
  const sections = template.sections || [];

  return (
    <Box
      data-testid="template-card"
      data-template-id={template.id}
      sx={{
        background: SP.white,
        border: `1px solid ${SP.lineSoft}`,
        borderRadius: '8px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform .22s ease, box-shadow .22s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 22px 40px -26px rgba(35,31,77,0.3)',
        },
      }}
    >
      {/* Thumbnail. Fixed height and clipped, so a nine-section template and a
          three-section one line up across the grid. */}
      <Box
        sx={{
          height: 118,
          p: '14px 16px',
          overflow: 'hidden',
          background: THUMB_BG,
          borderBottom: `1px solid ${SP.lineSoft}`,
        }}
      >
        <FormatSample format={template.format} variant="mini" />
      </Box>

      <Box sx={{ p: '16px 18px 18px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box
          sx={{
            fontFamily: FONT.mono,
            fontSize: 9.5,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: accent,
          }}
        >
          {CATEGORY_LABEL[template.category] || template.category}
        </Box>

        <Typography
          data-testid="template-name"
          sx={{ fontFamily: FONT.serif, fontWeight: 600, fontSize: 16.5, mt: '5px' }}
        >
          {template.name}
        </Typography>

        <Typography sx={{ color: SP.gray, fontSize: 12, lineHeight: 1.6, mt: '7px', flex: 1 }}>
          {template.description}
        </Typography>

        <Box sx={{ fontFamily: FONT.mono, fontSize: 10, color: SP.gray, mt: '10px' }}>
          {sections.length} sections
        </Box>

        <Box
          component="button"
          type="button"
          data-testid="template-preview-toggle"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          sx={{
            fontFamily: FONT.mono,
            fontSize: 10,
            color: SP.purple,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            mt: '8px',
            p: 0,
            textDecoration: 'underline',
          }}
        >
          {expanded ? 'Hide structure & sample' : 'Preview structure & sample'}
        </Box>

        {expanded && (
          <Box sx={{ mt: '8px' }} data-testid="template-structure">
            {sections.map((section, i) => (
              <Box key={`${template.id}-${i}`} sx={{ display: 'flex', gap: '8px', fontSize: 11, py: '2px' }}>
                <Box component="span" sx={{ fontFamily: FONT.mono, color: SP.gray, flexShrink: 0 }}>
                  {i + 1}.
                </Box>
                <Box component="span">
                  {section.title}
                  {/* The writing prompt lives here and nowhere else. It is
                      deliberately not seeded into the draft — a new manuscript
                      opens on its structure, not on words to delete. */}
                  {section.prompt && (
                    <Box sx={{ color: SP.gray, fontSize: 10.5, lineHeight: 1.5, mt: '2px' }}>
                      {section.prompt}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}

            <Box
              sx={{
                fontFamily: FONT.mono,
                fontSize: 9,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: SP.goldDeep,
                mt: '12px',
                mb: '6px',
              }}
            >
              Formatted sample
            </Box>
            <FormatSample format={template.format} variant="full" />

            {/* Templates whose format carries artwork say so, rather than
                implying the app will draw it. */}
            {template.visual && (
              <Box
                sx={{
                  mt: '10px',
                  border: `1.5px dashed ${SP.lineSoft}`,
                  borderRadius: '4px',
                  background: SP.lavender,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: '16px 10px',
                  gap: '5px',
                }}
              >
                <Box
                  sx={{
                    fontFamily: FONT.mono,
                    fontSize: 9.5,
                    color: SP.gray,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                  }}
                >
                  {template.visual}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Button
          variant="contained"
          data-testid="template-use"
          onClick={onUse}
          sx={{
            mt: '14px',
            fontFamily: FONT.mono,
            fontSize: 11,
            letterSpacing: '.02em',
            background: SP.purple,
            color: SP.white,
            borderRadius: '4px',
            boxShadow: 'none',
            '&:hover': { background: SP.gold, color: SP.purpleDeep, boxShadow: 'none' },
          }}
        >
          Use this template
        </Button>
      </Box>
    </Box>
  );
}
